/**
 * inventoryHelpers.ts — MADE-074
 *
 * Pure utility functions extracted from Inventory.tsx.
 * No side effects, no I/O — safe to unit test directly.
 */

import type { Material } from '../types/madebb.types';

/** Round stock values to 2dp to eliminate floating-point artifacts (MADE-192) */
export const formatStock = (n: number): number => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// Stock status
// ---------------------------------------------------------------------------

export interface StockStatus {
  label: 'Out of Stock' | 'Low Stock' | 'In Stock';
  color: 'error' | 'warning' | 'success';
}

/**
 * Determine stock status for a material.
 * Pure function — no component imports needed.
 */
export function getStockStatus(material: Material): StockStatus {
  if (material.currentStock === 0) {
    return { label: 'Out of Stock', color: 'error' };
  } else if (material.currentStock <= material.minStock && material.currentStock <= 10) {
    return { label: 'Low Stock', color: 'warning' };
  } else {
    return { label: 'In Stock', color: 'success' };
  }
}

// ---------------------------------------------------------------------------
// Hierarchy organisation
// ---------------------------------------------------------------------------

export interface HierarchyRow {
  material: Material;
  variants?: Material[];
  rollupStock?: number;
}

export interface OrganizeHierarchyOptions {
  materials: Material[];
  searchTerm?: string;
  sortBy?: 'status' | 'name' | 'buyOrProduce' | 'lastRestocked';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Organise a flat list of materials into a sorted, searchable hierarchy.
 * Masters appear first with their variants nested; standalone materials
 * are included as top-level rows.
 *
 * Pure function — accepts all inputs as arguments.
 */
export function organizeHierarchy({
  materials,
  searchTerm = '',
  sortBy = 'name',
  sortDirection = 'asc',
}: OrganizeHierarchyOptions): HierarchyRow[] {
  const hierarchy: HierarchyRow[] = [];
  const variantMap = new Map<string, Material[]>();

  // Group variants by parent
  materials.forEach(material => {
    if (material.parentMaterialId) {
      if (!variantMap.has(material.parentMaterialId)) {
        variantMap.set(material.parentMaterialId, []);
      }
      variantMap.get(material.parentMaterialId)!.push(material);
    }
  });

  // Apply search filter
  const filteredMaterials = searchTerm.trim()
    ? materials.filter(material => {
        const searchLower = searchTerm.toLowerCase();
        return (
          material.name.toLowerCase().includes(searchLower) ||
          material.type.toLowerCase().includes(searchLower) ||
          material.color?.toLowerCase().includes(searchLower) ||
          material.supplier?.toLowerCase().includes(searchLower) ||
          material.id.toLowerCase().includes(searchLower)
        );
      })
    : materials;

  // Process filtered materials
  filteredMaterials.forEach(material => {
    if (material.isMaster) {
      let variants = variantMap.get(material.id) || [];
      if (searchTerm.trim()) {
        variants = variants.filter(v => filteredMaterials.includes(v));
      }
      const rollupStock = Math.round(variants.reduce((sum, variant) => sum + variant.currentStock, 0) * 100) / 100;
      hierarchy.push({
        material: { ...material, currentStock: rollupStock },
        variants,
        rollupStock,
      });
    } else if (!material.parentMaterialId) {
      // Standalone material
      hierarchy.push({ material });
    } else if (searchTerm.trim()) {
      // Variant matches search — include its master if not already in hierarchy
      const parentMaterial = materials.find(m => m.id === material.parentMaterialId);
      if (parentMaterial && !hierarchy.find(h => h.material.id === parentMaterial.id)) {
        const variants = variantMap.get(parentMaterial.id) || [];
        const rollupStock = Math.round(variants.reduce((sum, variant) => sum + variant.currentStock, 0) * 100) / 100;
        hierarchy.push({
          material: { ...parentMaterial, currentStock: rollupStock },
          variants,
          rollupStock,
        });
      }
    }
    // Skip variants in normal mode — they appear under their masters
  });

  const STATUS_ORDER: Record<string, number> = {
    'Out of Stock': 0,
    'Low Stock': 1,
    'In Stock': 2,
  };

  // Sort
  hierarchy.sort((a, b) => {
    let compareA: any;
    let compareB: any;

    switch (sortBy) {
      case 'status': {
        const sA = getStockStatus(a.material);
        const sB = getStockStatus(b.material);
        compareA = STATUS_ORDER[sA.label] ?? 0;
        compareB = STATUS_ORDER[sB.label] ?? 0;
        break;
      }
      case 'name':
        compareA = a.material.name.toLowerCase();
        compareB = b.material.name.toLowerCase();
        break;
      case 'buyOrProduce':
        compareA = a.material.buyOrProduce;
        compareB = b.material.buyOrProduce;
        break;
      case 'lastRestocked':
        compareA = new Date(a.material.lastRestocked).getTime();
        compareB = new Date(b.material.lastRestocked).getTime();
        break;
      default:
        return 0;
    }

    if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
    if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return hierarchy;
}

// ---------------------------------------------------------------------------
// Formatting utilities
// ---------------------------------------------------------------------------

export function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}
